/**
 * How a failed API call is reported to Sentry.
 *
 * WHY THIS EXISTS — DASHBOARD-2
 * -----------------------------
 * `App.tsx` installs ONE global `MutationCache.onError` that captures every
 * failed mutation. An `ApiError` is thrown from a single place
 * (`apiClient.ts` → `fetchApi` / `fetchApiWithFormData`), so every one of them
 * carries the SAME one-frame stack and Sentry groups them all into a single
 * issue whose title is simply whichever failure arrived last.
 *
 * Measured on 2026-08-31: DASHBOARD-2 held **13 distinct messages** over 90
 * days under one title — "Cannot book a court for a time that has already
 * passed.", "Cannot create recurring events: … conflicting bookings",
 * "No Stripe Checkout session is associated with this blockade",
 * "OrganizationLocation not found ('')", "Internal server error" (11 of 24
 * events), and more. Two separate defects follow from that, and this module
 * fixes both:
 *
 * 1. **A reporting defect.** The event names no endpoint, no method and no
 *    status — `culprit` is literally `/` — so nothing about it can be triaged,
 *    and a fix that lands for one member leaves the group open and "regressed"
 *    for the next unrelated member (run 11 fixed the comped-game checkbox and
 *    the group never closed). Remedy: identify the request in the fingerprint,
 *    so each endpoint+status gets its own issue.
 *
 * 2. **Expected business outcomes reported as errors.** Most of those messages
 *    are the API deliberately refusing an operator action and saying why; the
 *    dashboard already shows the text in a toast. The API does not report its
 *    own 4xx either — `api/src/instrument.ts`'s `sentryBeforeSend` drops 4xx
 *    HttpExceptions — so capturing the same rejection on the client is the
 *    platform contradicting itself. Remedy: leave a breadcrumb instead of an
 *    error.
 *
 * The filter is deliberately narrow (ledger D2 — a capture filter is only ever
 * allowed for genuinely expected business outcomes, never for 5xx, data
 * corruption or money paths):
 *   - 5xx, and anything that is not an `ApiError`, is still captured;
 *   - 408 and 429 are still captured (a stalled request and a rate-limit burst
 *     are signals, not outcomes — the same carve-out `expectedErrors.ts` makes
 *     for `auth/too-many-requests` and the API's `isPermanentError` makes for
 *     Stripe 429s);
 *   - nothing is silently dropped: a skipped rejection becomes a breadcrumb, so
 *     it is attached to whatever error IS reported next.
 */
import type { Breadcrumb } from '@sentry/react';
import { ApiError, ApiRequestFailure, TokenRefreshFailure } from '../apiClient';
import type { ApiRequestIdentity } from '../apiClient';

/** A path segment that is a bare row id. */
const NUMERIC_SEGMENT = /^\d+$/;
/** A path segment that is a UUID (organization ids, campaign ids, …). */
const UUID_SEGMENT =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A path segment that is a date or an ISO timestamp (calendar ranges). */
const DATE_SEGMENT = /^\d{4}-\d{2}-\d{2}([T ].*)?$/;

/** 4xx statuses that are NOT an expected outcome of an operator action. */
const REPORTABLE_CLIENT_STATUSES: ReadonlySet<number> = new Set([408, 429]);

/**
 * Collapses the variable parts of an endpoint so that N calls to the same route
 * fingerprint as one issue.
 *
 * Deliberately CONSERVATIVE: only a segment that is entirely a number, a UUID
 * or a date is templated. A word that merely contains digits (`v2`, `notif-123`)
 * is left alone — over-templating silently merges two different endpoints back
 * into one group, which is the very defect this module exists to fix, and it is
 * the harder failure to notice afterwards.
 *
 * The query string is dropped: it carries ids, ISO dates and filter state, and
 * none of it distinguishes one route from another.
 */
export function templateEndpoint(endpoint: string): string {
    const path = endpoint.split('?')[0].replace(/^\/+/, '').replace(/\/+$/, '');
    if (!path) return '/';

    return path
        .split('/')
        .map((segment) => {
            if (NUMERIC_SEGMENT.test(segment)) return ':id';
            if (UUID_SEGMENT.test(segment)) return ':id';
            if (DATE_SEGMENT.test(segment)) return ':date';
            return segment;
        })
        .join('/');
}

/**
 * True when this rejection is the API deliberately refusing an operator action
 * and telling them why — an outcome, not a fault.
 */
export function isExpectedApiRejection(error: ApiError): boolean {
    return (
        error.status >= 400 &&
        error.status < 500 &&
        !REPORTABLE_CLIENT_STATUSES.has(error.status) &&
        typeof error.message === 'string' &&
        error.message.length > 0
    );
}

/**
 * The route label used in fingerprints, tags and breadcrumbs.
 *
 * `ApiError`s thrown before the request identity was recorded (and any future
 * caller that constructs one by hand) have no `request`, so they fall back to a
 * stable placeholder rather than to `undefined` — an unknown route must not
 * fingerprint as a *different* unknown route on every event.
 */
function routeLabel(error: { request?: ApiRequestIdentity }): string {
    if (!error.request) return 'unknown';
    return `${error.request.method.toUpperCase()} ${templateEndpoint(
        error.request.endpoint,
    )}`;
}

/**
 * What Sentry should be told about a failed mutation.
 *
 * `null` means "not an API rejection — handle it however you handled it before"
 * (a Firebase auth error, a thrown string, a bug in a component callback).
 */
export type ApiErrorReport =
    | { kind: 'expected'; breadcrumb: Breadcrumb }
    | {
        kind: 'capture';
        context: {
            fingerprint: string[];
            tags: Record<string, string>;
            extra: Record<string, unknown>;
        };
    };

/**
 * Classifies a mutation failure. Returns `null` for anything that is not an
 * `ApiError`, so the caller keeps its existing behaviour for those.
 */
export function classifyApiError(error: unknown): ApiErrorReport | null {
    // Before the `ApiError` guard, and never 'expected' (D2). A failure that
    // carried no API message cannot be "an outcome the operator was shown",
    // because there was nothing to show them — and a transport failure may be
    // our own API being unreachable, which is a signal. The one case where it
    // is legitimately not an error is an offline browser, decided one layer up
    // in `queryErrorReporting.ts`.
    //
    // Delegated to `captureContextFor` rather than re-testing the types here:
    // this function used to carry its own `instanceof ApiRequestFailure`
    // branch, which is a FORK of that ladder (D59) — a type added to one and
    // not the other is captured with full context on the query path and with
    // none on the mutation path, and the symptom is that both keep working.
    if (!(error instanceof ApiError)) {
        const context = captureContextFor(error);
        return context ? { kind: 'capture', context } : null;
    }

    const route = routeLabel(error);
    const status = String(error.status);

    if (isExpectedApiRejection(error)) {
        return {
            kind: 'expected',
            breadcrumb: {
                category: 'api',
                type: 'http',
                level: 'warning',
                message: `${route} refused (${status}): ${error.message}`,
                data: {
                    endpoint: error.request?.endpoint,
                    method: error.request?.method,
                    status: error.status,
                    code: error.code,
                },
            },
        };
    }

    return { kind: 'capture', context: apiErrorCaptureContext(error) };
}

/**
 * The Sentry context that makes one `ApiError` distinguishable from another.
 *
 * Exported because the QUERY path (`queryErrorReporting.ts`) captures a
 * different set of statuses than the mutation path and still needs the same
 * fingerprint — one definition, so two reporting paths cannot drift into
 * grouping the same endpoint two ways.
 */
export function apiErrorCaptureContext(error: ApiError): {
    fingerprint: string[];
    tags: Record<string, string>;
    extra: Record<string, unknown>;
} {
    const route = routeLabel(error);
    const status = String(error.status);

    return {
        // Without this every ApiError shares one group (DASHBOARD-2): the
        // stack is a single frame in apiClient.ts and the default
        // fingerprint is that stack.
        fingerprint: ['api-error', route, status],
        tags: {
            'api.route': route,
            'api.status': status,
            ...(error.code ? { 'api.code': error.code } : {}),
        },
        extra: {
            // The raw endpoint (query string included) is what a reader
            // actually needs to reproduce the call; the templated form only
            // exists to group.
            endpoint: error.request?.endpoint ?? null,
            method: error.request?.method ?? null,
            status: error.status,
            code: error.code ?? null,
        },
    };
}

/**
 * The Sentry context for a request that failed with no API message —
 * DASHBOARD-1V / -40 / -3K.
 *
 * ⚠️ THE BROWSER'S MESSAGE IS DELIBERATELY ABSENT FROM THE FINGERPRINT. That
 * message is the entire reason one condition was filed as two issues: WebKit
 * writes `Load failed` and Chromium `Failed to fetch` for the same dropped
 * request, and the DEFAULT fingerprint is the message. Grouping on
 * route + kind (+ status, where there is one) is what collapses them, so
 * putting `underlying` anywhere near the fingerprint would restore the defect.
 * It goes in `extra`, where a reader can still see it.
 *
 * `status` is in the `unreadable` fingerprint and absent from `network`'s: a
 * 502 and a 404-with-an-empty-body are different problems worth separate
 * issues, whereas a transport failure has no status to separate by (it is
 * always 0).
 */
export function requestFailureCaptureContext(error: ApiRequestFailure): {
    fingerprint: string[];
    tags: Record<string, string>;
    extra: Record<string, unknown>;
} {
    const route = routeLabel(error);
    const status = String(error.status);
    const underlying =
        error.underlying instanceof Error
            ? `${error.underlying.name}: ${error.underlying.message}`
            : error.underlying ?? null;

    return {
        fingerprint:
            error.kind === 'network'
                ? ['api-request-failure', 'network', route]
                : ['api-request-failure', 'unreadable', route, status],
        tags: {
            'api.route': route,
            'api.failure': error.kind,
            // Omitted for `network`, where 0 is a placeholder rather than a
            // status: a tag that reads `api.status: 0` invites a filter on it.
            ...(error.kind === 'unreadable' ? { 'api.status': status } : {}),
        },
        extra: {
            endpoint: error.request.endpoint,
            method: error.request.method,
            status: error.kind === 'unreadable' ? error.status : null,
            kind: error.kind,
            underlying,
        },
    };
}

/**
 * The Sentry context for a failed token refresh — DASHBOARD-43 / MYSPORTSET-75.
 *
 * There is no route to group by: the request goes to Google's `securetoken`
 * endpoint, not to our API, and it is the same URL every time. What
 * distinguishes one of these from another is WHY it failed, so the fingerprint
 * is `kind` + `status` — which also separates the three populations an
 * operator would act on differently: a dropped request (nothing to do), a
 * refused credential (the operator really is signed out), and a provider
 * outage (wait).
 *
 * ⚠️ THE BROWSER'S MESSAGE IS DELIBERATELY ABSENT FROM THE FINGERPRINT, for the
 * same reason it is absent from `requestFailureCaptureContext`'s: WebKit writes
 * `Load failed` and Chromium `Failed to fetch` for one condition, and the
 * default fingerprint IS the message, so DASHBOARD-43 and its Chromium twin
 * would be filed as two unrelated issues. It goes in `extra`.
 *
 * `auth.revoked` is tagged because it is the one field that says whether the
 * operator was signed out — the behaviour this type exists to gate — and a
 * Sentry search is how anyone would ever notice that gate regressing.
 */
export function tokenRefreshFailureCaptureContext(error: TokenRefreshFailure): {
    fingerprint: string[];
    tags: Record<string, string>;
    extra: Record<string, unknown>;
} {
    const status = String(error.status);
    const underlying =
        error.underlying instanceof Error
            ? `${error.underlying.name}: ${error.underlying.message}`
            : error.underlying ?? null;

    return {
        fingerprint: ['token-refresh-failure', error.kind, status],
        tags: {
            'auth.refresh': error.kind,
            'auth.revoked': String(error.revoked),
            // Omitted for `network`, where 0 is a placeholder rather than a
            // status — the same rule `requestFailureCaptureContext` applies.
            ...(error.kind === 'network' ? {} : { 'auth.refresh.status': status }),
        },
        extra: {
            kind: error.kind,
            status: error.kind === 'network' ? null : error.status,
            revoked: error.revoked,
            signedOut: error.revoked,
            underlying,
        },
    };
}

/**
 * The capture context for EITHER failure type, or `undefined` for anything
 * else.
 *
 * Exists so the two reporting paths cannot drift: `queryErrorReporting.ts`
 * captures a different SET of failures than the mutation path, but every
 * failure either of them captures must group the same way. A second
 * `instanceof` ladder is a fork (D59).
 */
export function captureContextFor(error: unknown):
    | { fingerprint: string[]; tags: Record<string, string>; extra: Record<string, unknown> }
    | undefined {
    if (error instanceof TokenRefreshFailure)
        return tokenRefreshFailureCaptureContext(error);
    if (error instanceof ApiRequestFailure) return requestFailureCaptureContext(error);
    if (error instanceof ApiError) return apiErrorCaptureContext(error);
    return undefined;
}
